import { PayloadAction, Draft } from "@reduxjs/toolkit";
import { DocumentStatusCountDto } from "../../shared/dtos/common/DocumentStatusCountDto";
import { DocumentTotalsDto } from "../../shared/dtos/common/DocumentTotalsDto";
import { PaginationDto } from "../../shared/dtos/common/PaginationDto";
import { ServerStatusPayload } from "../../shared/interfaces/ServerStatusPayload";

export interface CrudState<T, SummaryT = T> {
  items: SummaryT[];
  item: T | null;
  pagination: Partial<PaginationDto>;
  totals: Partial<DocumentTotalsDto>;
  statusCount: Partial<DocumentStatusCountDto>;

  loadingFetchAll: boolean;
  loadingFetchOne: boolean;
  loadingCreate: boolean;
  loadingUpdate: boolean;
  loadingDelete: boolean;

  errorFetchAll: string | null;
  errorFetchOne: string | null;
  errorCreate: string | null;
  errorUpdate: string | null;
  errorDelete: string | null;

  serverStatus: ServerStatusPayload | null;
}

export interface CrudReducers<T, SummaryT> {
  setLoadingFetchAll: (state: Draft<CrudState<T, SummaryT>>) => void;
  setErrorFetchAll: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<string | null>,
  ) => void;
  setItems: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<SummaryT[]>,
  ) => void;
  setPagination: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<Partial<PaginationDto>>,
  ) => void;
  setTotals: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<Partial<DocumentTotalsDto>>,
  ) => void;
  setStatusCount: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<Partial<DocumentStatusCountDto>>,
  ) => void;

  setLoadingFetchOne: (state: Draft<CrudState<T, SummaryT>>) => void;
  setErrorFetchOne: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<string | null>,
  ) => void;
  setItem: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<T | null>,
  ) => void;

  setLoadingCreate: (state: Draft<CrudState<T, SummaryT>>) => void;
  setErrorCreate: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<string | null>,
  ) => void;
  createSuccess: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<SummaryT>,
  ) => void;

  setLoadingUpdate: (state: Draft<CrudState<T, SummaryT>>) => void;
  setErrorUpdate: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<string | null>,
  ) => void;
  updateSuccess: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<SummaryT>,
  ) => void;

  setLoadingDelete: (state: Draft<CrudState<T, SummaryT>>) => void;
  setErrorDelete: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<string | null>,
  ) => void;
  deleteSuccess: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<string>,
  ) => void;

  setServerStatus: (
    state: Draft<CrudState<T, SummaryT>>,
    action: PayloadAction<ServerStatusPayload | null>,
  ) => void;
}

export const getInitialCrudState = <T, SummaryT = T>(): CrudState<
  T,
  SummaryT
> => ({
  items: [],
  item: null,
  pagination: {},
  totals: {},
  statusCount: {},

  loadingFetchAll: false,
  loadingFetchOne: false,
  loadingCreate: false,
  loadingUpdate: false,
  loadingDelete: false,

  errorFetchAll: null,
  errorFetchOne: null,
  errorCreate: null,
  errorUpdate: null,
  errorDelete: null,

  serverStatus: null,
});

export const generateCrudReducers = <
  T extends { id: string },
  SummaryT extends { id: string } = T,
>(): CrudReducers<T, SummaryT> => {
  return {
    // Fetch All
    setLoadingFetchAll(state) {
      state.loadingFetchAll = true;
      state.errorFetchAll = null;
    },
    setErrorFetchAll(state, action) {
      state.loadingFetchAll = false;
      state.errorFetchAll = action.payload;
    },
    setItems(state, action) {
      state.loadingFetchAll = false;
      state.items = action.payload as unknown as typeof state.items;
    },
    setPagination(state, action) {
      state.pagination = action.payload as unknown as typeof state.pagination;
    },
    setTotals(state, action) {
      state.totals = action.payload as unknown as typeof state.totals;
    },
    setStatusCount(state, action) {
      state.statusCount = action.payload as unknown as typeof state.statusCount;
    },

    // Fetch One
    setLoadingFetchOne(state) {
      state.loadingFetchOne = true;
      state.errorFetchOne = null;
    },
    setErrorFetchOne(state, action) {
      state.loadingFetchOne = false;
      state.errorFetchOne = action.payload;
    },
    setItem(state, action) {
      state.loadingFetchOne = false;
      state.item = action.payload as unknown as typeof state.item;
    },

    // Create
    setLoadingCreate(state) {
      state.loadingCreate = true;
      state.errorCreate = null;
    },
    setErrorCreate(state, action) {
      state.loadingCreate = false;
      state.errorCreate = action.payload;
    },
    createSuccess(state, action) {
      state.loadingCreate = false;
      // Immer allows direct unshift, safely mutating the draft array
      state.items.unshift(action.payload as unknown as (typeof state.items)[0]);
    },

    // Update
    setLoadingUpdate(state) {
      state.loadingUpdate = true;
      state.errorUpdate = null;
    },
    setErrorUpdate(state, action) {
      state.loadingUpdate = false;
      state.errorUpdate = action.payload;
    },
    updateSuccess(state, action) {
      state.loadingUpdate = false;
      const index = state.items.findIndex((i) => i.id === action.payload.id);
      if (index !== -1) {
        state.items[index] =
          action.payload as unknown as (typeof state.items)[0];
      }

      if (state.item && state.item.id === action.payload.id) {
        // Object.assign safely mutates the nested draft directly
        Object.assign(state.item, action.payload);
      }
    },

    // Delete
    setLoadingDelete(state) {
      state.loadingDelete = true;
      state.errorDelete = null;
    },
    setErrorDelete(state, action) {
      state.loadingDelete = false;
      state.errorDelete = action.payload;
    },
    deleteSuccess(state, action) {
      state.loadingDelete = false;
      state.items = state.items.filter(
        (i) => i.id !== action.payload,
      ) as unknown as typeof state.items;
      if (state.item && state.item.id === action.payload) {
        state.item = null as unknown as typeof state.item;
      }
    },

    // Global Server Status
    setServerStatus(state, action) {
      state.serverStatus =
        action.payload as unknown as typeof state.serverStatus;
    },
  };
};
